/**
 * ResetPassword.tsx — where an emailed link lands (?token=…): choose a password. Used for invitations ("Choose your
 * password") and for "forgot password". An expired or already-used link (?error=INVALID_TOKEN, or a rejected token)
 * explains what to do instead of showing a dead form.
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PASSWORD_MAX, passwordProblem } from "@demandhub-cms/shared";
import { ApiError, authApi, errorText } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { AuthShell } from "@/components/auth/AuthShell";
import { PasswordInput } from "@/components/auth/PasswordInput";

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dead, setDead] = useState(params.get("error") !== null || !token);

  if (dead) {
    return (
      <AuthShell title="This link no longer works">
        <p className="mt-2 text-base text-neutral-600">It may have expired, or it was already used. Links work once.</p>
        <div className="mt-8 space-y-3">
          <Button asChild className="w-full" size="lg">
            <Link to="/forgot-password">Send me a new link</Link>
          </Button>
          <p className="text-sm text-neutral-500">Just invited and the link has expired? Ask a manager to send you a new one.</p>
        </div>
      </AuthShell>
    );
  }

  const mismatch = again.length > 0 && again !== password;
  const blocked = Boolean(passwordProblem(password)) || password !== again || password.length > PASSWORD_MAX;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (blocked || !token) return;
    setError(null);
    setBusy(true);
    try {
      await authApi.resetPassword(token, password);
      navigate("/login?reset=1", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "INVALID_TOKEN") setDead(true);
      else setError(errorText(err, "Could not save the password. Try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Choose your password" subtitle="Use a long phrase you will remember, or let your password manager make one.">
      {error && <Alert tone="danger" className="mt-6">{error}</Alert>}
      <form onSubmit={submit} className="mt-8 space-y-5">
        <PasswordInput id="new-password" label="New password" autoComplete="new-password" value={password} onChange={setPassword} check autoFocus />
        <div>
          <PasswordInput id="new-password-again" label="Type it again" autoComplete="new-password" value={again} onChange={setAgain} />
          {mismatch && <p className="mt-1.5 text-xs text-danger-700">The two passwords do not match.</p>}
        </div>
        <Button type="submit" className="w-full" size="lg" loading={busy} disabled={blocked}>
          {busy ? "Saving…" : "Save password"}
        </Button>
      </form>
    </AuthShell>
  );
}
